import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useMemo } from 'react';
import { Search, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Video } from '@/types/hierarchicalFolders';
import { VideoCardPremium } from './VideoCardPremium';
import { useDebounce } from '@/hooks/useDebounce';

interface VideoGridPremiumProps {
  videos: Video[];
  onVideoClick?: (video: Video) => void;
  onVideoEdit?: (video: Video) => void;
  onVideoMove?: (video: Video) => void;
  onVideoDuplicate?: (video: Video) => void;
  onVideoDelete?: (video: Video) => void;
  onVideoStats?: (video: Video) => void;
}

type SortOption = 'name-az' | 'recent' | 'plays' | 'duration';

export function VideoGridPremium({
  videos,
  onVideoClick,
  onVideoEdit,
  onVideoMove,
  onVideoDuplicate,
  onVideoDelete,
  onVideoStats,
}: VideoGridPremiumProps) {
  useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Filter and sort videos
  const processedVideos = useMemo(() => {
    let filtered = videos;

    // Apply search filter
    if (debouncedSearch) {
      filtered = filtered.filter((video) =>
        video.title.toLowerCase().includes(debouncedSearch.toLowerCase())
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name-az':
          return a.title.localeCompare(b.title);
        case 'recent':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'plays':
          return (b.plays || 0) - (a.plays || 0);
        case 'duration':
          return b.duration - a.duration;
        default:
          return 0;
      }
    });

    return sorted;
  }, [videos, debouncedSearch, sortBy]);

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'name-az', label: translateInterfaceText("Nome A-Z") },
    { value: 'recent', label: translateInterfaceText("Mais recente") },
    { value: 'plays', label: translateInterfaceText("Mais reproduzido") },
    { value: 'duration', label: translateInterfaceText("Maior duração") },
  ];

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
          <input
            type="text"
            placeholder={translateInterfaceText("Buscar vídeos...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "w-full pl-10 pr-4 py-2.5 text-sm",
              "bg-white border-b-2 border-[#E5E7EB]",
              "focus:border-[#4169E1] focus:outline-none",
              "transition-colors duration-200",
              "placeholder:text-[#9CA3AF]"
            )}
          />
        </div>

        {/* Sort Chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setSortBy(option.value)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium",
                "transition-all duration-200",
                sortBy === option.value
                  ? "bg-[#4169E1] text-white shadow-sm"
                  : "bg-[#EEEEEE] text-[#6B7280] hover:bg-[#E5E7EB]"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Videos Grid */}
      {processedVideos.length > 0 ? (
        <div
          className={cn(
            "grid gap-7",
            "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
            "animate-fade-scale"
          )}
        >
          {processedVideos.map((video) => (
            <VideoCardPremium
              key={video.id}
              video={video}
              onClick={() => onVideoClick?.(video)}
              onEdit={() => onVideoEdit?.(video)}
              onMove={() => onVideoMove?.(video)}
              onDuplicate={() => onVideoDuplicate?.(video)}
              onDelete={() => onVideoDelete?.(video)}
              onViewStats={() => onVideoStats?.(video)}
            />
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-4">
            <FolderOpen className="h-8 w-8 text-[#9CA3AF]" />
          </div>
          <h3 className="text-lg font-semibold text-[#111827] mb-2">
            {debouncedSearch ? translateInterfaceText("Nenhum vídeo encontrado") : translateInterfaceText("Pasta vazia")}
          </h3>
          <p className="text-sm text-[#6B7280] text-center max-w-sm">
            {debouncedSearch
              ? translateInterfaceText("Tente ajustar sua busca ou limpar os filtros.")
              : translateInterfaceText("Adicione vídeos a esta pasta para começar a organizar seu conteúdo.")}
          </p>
        </div>
      )}
    </div>
  );
}
